import { remark } from 'remark';
import remarkGfm from 'remark-gfm';

import { CaraWorkspaceMessageRole, type CaraWorkspaceFieldsFragment } from '~/api/graphql';

import { defaultDocumentVersion } from '~/components/documents/document-versions';
import { isDocumentArtifact } from '~/components/documents/documents.utils';

type Workspace = CaraWorkspaceFieldsFragment;
type Message = Workspace['messages'][0];
type Artifact = Workspace['artifacts'][0];

/**
 * h4 and below are detail within a section rather than structure, and indenting
 * them makes the rail unreadable at 20rem wide.
 */
const MAX_HEADING_DEPTH = 3;

/** Long enough to tell two prompts apart, short enough to stay one line. */
const LABEL_MAX_CHARS = 100;

/**
 * The subset of mdast this file reads. `@types/mdast` is only a transitive
 * dependency, so Yarn PnP refuses to import it.
 */
type MdastNode = {
  type: string;
  depth?: number;
  value?: string;
  children?: MdastNode[];
};

/**
 * Walk an mdast tree in document order. Hand-rolled like `walk` in
 * lib/footnote-citations.ts: `unist-util-visit` is also only a transitive
 * dependency.
 */
const walk = (node: MdastNode, visit: (node: MdastNode) => void) => {
  visit(node);
  for (const child of node.children ?? []) {
    walk(child, visit);
  }
};

/**
 * Flattens a node's text, dropping the emphasis and link syntax around it. A
 * node with neither text nor children contributes nothing, which is how an
 * image, an html tag and a footnote marker fall out of a label.
 */
const nodeText = (node: MdastNode): string =>
  node.type === 'text' || node.type === 'inlineCode'
    ? (node.value ?? '')
    : (node.children ?? []).map(nodeText).join('');

const collapseWhitespace = (text: string) => text.replace(/\s+/g, ' ').trim();

/**
 * A GFM footnote marker, which in a Cara response is a citation.
 *
 * A reference only becomes a `footnoteReference` node once its definition has
 * been parsed too, and definitions arrive at the end of a response -- so a
 * heading written early in a stream carries markers that are still plain text.
 * Dropping them by shape as well as by node type keeps `[^1]` out of the label
 * in the seconds before the definition lands.
 */
const FOOTNOTE_MARKER = /\[\^[^\]]*\]/g;

/** A heading or a prompt as the one line of plain text the rail shows. */
const labelText = (node: MdastNode) =>
  collapseWhitespace(nodeText(node).replace(FOOTNOTE_MARKER, ''));

const truncate = (text: string) =>
  text.length > LABEL_MAX_CHARS ? `${text.slice(0, LABEL_MAX_CHARS).trimEnd()}…` : text;

/**
 * GFM, because that is what renders the response: footnotes, strikethrough and
 * bare URLs are nodes to read the text out of rather than punctuation to leave
 * sitting in a label.
 */
const processor = remark().use(remarkGfm);

const parse = (markdown: string) => processor.parse(markdown) as unknown as MdastNode;

export type OutlineHeading = {
  /** 1, 2 or 3, mirroring the markdown heading level. */
  depth: number;
  text: string;
};

/**
 * The h1-h3 headings of a markdown snippet, in document order.
 *
 * Parsing rather than matching `^#{1,3} ` is what keeps a `#` line inside a
 * fenced code block out of the outline and brings a setext (`===` underlined)
 * heading in.
 */
export const markdownHeadings = (markdown: string): OutlineHeading[] => {
  const headings: OutlineHeading[] = [];

  walk(parse(markdown), (node) => {
    if (node.type !== 'heading' || node.depth == null || node.depth > MAX_HEADING_DEPTH) {
      return;
    }
    const text = labelText(node);
    if (text) {
      headings.push({ depth: node.depth, text });
    }
  });

  return headings;
};

/** The first block of a snippet that carries any text, as one plain line. */
const leadingText = (markdown: string): string => {
  for (const block of parse(markdown).children ?? []) {
    const text = labelText(block);
    if (text) {
      return text;
    }
  }
  return '';
};

export type OutlineEntry = {
  /** Matches the `data-message-id` on the rendered message, which is what the rail scrolls to. */
  messageId: string;
  /**
   * Position of the heading among the h1-h3 elements the message renders, or
   * undefined when the entry is the message itself.
   */
  headingIndex?: number;
  /** 0 for a message, 1-3 for a heading inside one. */
  depth: number;
  label: string;
};

/**
 * The prompt a user turn contributes to the outline. A turn started by picking
 * a follow-up carries no text of its own, and one that only attaches files
 * carries none either, so both fall back to what the turn did have.
 */
const userEntries = (message: Message): OutlineEntry[] => {
  const label = truncate(leadingText(message.content)) || attachmentLabel(message);
  return label ? [{ messageId: message.id, depth: 0, label }] : [];
};

const attachmentLabel = (message: Message): string => {
  const count = message.files.length;
  if (count === 0) {
    return '';
  }
  return count === 1 ? '1 attachment' : `${count} attachments`;
};

/**
 * Parsed headings for one message, keyed by message id.
 *
 * A watch tick replaces the whole message list, so an outline rebuilt from it
 * would re-parse the entire conversation while the newest response streams.
 * Parsing costs about 6ms for a 10KB response and 54ms for a 31KB one, which is
 * far too much to spend per tick — and all of it is spent re-reading messages
 * that are already finished and cannot change. Only the entry whose content
 * moved is re-parsed.
 */
const headingsByMessage = new Map<string, { content: string; headings: OutlineHeading[] }>();

const cachedHeadings = (message: Message): OutlineHeading[] => {
  const cached = headingsByMessage.get(message.id);
  if (cached?.content === message.content) {
    return cached.headings;
  }
  const headings = markdownHeadings(message.content);
  headingsByMessage.set(message.id, { content: message.content, headings });
  return headings;
};

/**
 * An agent turn contributes its headings and nothing else: a response with no
 * headings is a paragraph or two, which the preceding prompt already stands for.
 */
const agentEntries = (message: Message): OutlineEntry[] => {
  const headings = cachedHeadings(message).map((heading, headingIndex) => ({
    messageId: message.id,
    headingIndex,
    depth: heading.depth,
    label: heading.text
  }));

  // Adjust the depth of the headings. Sometimes the agent will use h2 or h3,
  // so the depth may not be consistent across messages. We normalize the depth
  // so that all headings are shifted back to depth 1.
  const minDepth = Math.min(...headings.map((heading) => heading.depth));
  return headings.map((heading) => ({
    ...heading,
    depth: heading.depth - minDepth + 1
  }));
};

/**
 * The conversation as a list of jump targets: each user prompt, followed by the
 * section headings of the response it drew.
 */
export const buildMessageOutline = (workspace: Workspace): OutlineEntry[] => {
  const entries = workspace.messages.flatMap((message) =>
    message.role === CaraWorkspaceMessageRole.User ? userEntries(message) : agentEntries(message)
  );

  // Bound the cache to the conversation in front of the user, so moving between
  // workspaces in one session does not accumulate the ones left behind.
  const live = new Set(workspace.messages.map((message) => message.id));
  for (const id of headingsByMessage.keys()) {
    if (!live.has(id)) {
      headingsByMessage.delete(id);
    }
  }

  return entries;
};

export type DocumentOutline = {
  /**
   * The version each artifact opens on (its Final, else its latest), oldest first by when that
   * version was made, so a document whose Final is not its latest sits where its Final was made.
   */
  artifacts: Artifact[];
  files: Workspace['messages'][0]['files'][0][];
};

/**
 * The documents a workspace has produced and the files it was given.
 *
 * `workspace.artifacts` carries every version of every artifact; each gets one
 * row, for the version opening it shows (its Final, else its latest).
 */
export const buildDocumentOutline = (workspace: Workspace): DocumentOutline => {
  const versionsById = new Map<string, Artifact[]>();

  for (const artifact of workspace.artifacts) {
    if (!isDocumentArtifact(artifact.type)) {
      continue;
    }
    const versions = versionsById.get(artifact.id);
    if (versions) versions.push(artifact);
    else versionsById.set(artifact.id, [artifact]);
  }

  const filesMap = new Map<string, Workspace['messages'][0]['files'][0]>();
  for (const file of workspace.files) {
    filesMap.set(file.id, file);
  }
  for (const message of workspace.messages) {
    for (const file of message.files) {
      filesMap.set(file.id, file);
    }
  }

  const artifacts = [...versionsById.values()]
    .map((versions) => defaultDocumentVersion(versions))
    .filter((artifact): artifact is Artifact => !!artifact)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const files = [...filesMap.values()].sort((a, b) => a.name.localeCompare(b.name));

  return { artifacts, files };
};
